import React, { FC, useContext, useEffect, useState, useMemo } from "react";

import { ICareerSheetAnswer, CareerChallengeSheetFormData } from "../../state";
import { Context } from "../../store/student";
import Selector from "../../atoms/Career/Selector";
import ChallengeSheetsModal from "../../organisms/Career/ChallengeSheetsModal";

import Question from "./Question";
import FormActions from "./FormActions";
import styles from "./CareerSheet.module.scss";

type Props = {
  calling: boolean;
  isCreateTopicsFailed: boolean;
  saveCareerSheetAnswers: (id: number, answers: ICareerSheetAnswer[]) => void;
  applyForCareerMapGeneration: (id: number) => void;
  fetchCareerChallengeSheet: () => void;
  createCareerChallengeSheets: () => void;
  createCareerChallengeTopics: (id: number) => void;
  saveCareerChallengeSheets: (
    id: number,
    formData: CareerChallengeSheetFormData,
  ) => void;
  submitCareerChallengeSheets: (
    id: number,
    formData: CareerChallengeSheetFormData,
  ) => void;
};

const CareerSheet: FC<Props> = ({
  calling,
  isCreateTopicsFailed,
  saveCareerSheetAnswers,
  applyForCareerMapGeneration,
  createCareerChallengeSheets,
  createCareerChallengeTopics,
  saveCareerChallengeSheets,
  submitCareerChallengeSheets,
}) => {
  const { contextState } = useContext(Context);
  const [selectedTermNo, setSelectedTermNo] = useState<number>(0);
  const [answersMap, setAnswersMap] = useState<
    Record<number, ICareerSheetAnswer[]>
  >({});
  const [isOpenChallengeSheet, setIsOpenChallengeSheet] = useState(false);

  const latestSheet = contextState.career_latest_sheet;

  const selectedSheet = (() => {
    const sheet = contextState.career_sheets?.find(sheet => {
      return sheet.term_no === selectedTermNo;
    });

    return sheet;
  })();

  const isSheetCompleted = !!selectedSheet?.completed_at;

  const defaultAnswers = useMemo(() => {
    return (
      selectedSheet?.careergear_sheet_issues?.flatMap(issue =>
        issue.careergear_sheet_questions.map(question => ({
          careergear_sheet_question_id: question.id,
          content: question.careergear_sheet_answer?.content ?? "",
        })),
      ) ?? []
    );
  }, [selectedSheet?.careergear_sheet_issues]);

  const currentAnswers = answersMap[selectedTermNo] ?? [];

  const isQuestionsCompletedForTermNo1 = (() => {
    if (selectedTermNo !== 1) return true;

    return currentAnswers.every(answer => answer.content.trim().length > 0);
  })();

  const handleChangeAnswers = (questionId: number, content: string) => {
    setAnswersMap(prev => {
      const prevAnswers = prev[selectedTermNo] ?? [];
      const index = prevAnswers.findIndex(
        a => a.careergear_sheet_question_id === questionId,
      );

      const updatedAnswers = [...prevAnswers];
      if (index === -1) {
        updatedAnswers.push({
          careergear_sheet_question_id: questionId,
          content,
        });
      } else {
        updatedAnswers[index] = {
          ...updatedAnswers[index],
          content,
        };
      }

      return {
        ...prev,
        [selectedTermNo]: updatedAnswers,
      };
    });
  };

  const getAnswerByQuestionId = (id: number) => {
    const answer = currentAnswers.find(
      a => a.careergear_sheet_question_id === id,
    );

    return answer?.content ?? "";
  };

  useEffect(() => {
    const isAlreadyInitialized = answersMap[selectedTermNo] !== undefined;

    if (!isAlreadyInitialized && defaultAnswers.length > 0) {
      setAnswersMap(prev => ({
        ...prev,
        [selectedTermNo]: defaultAnswers,
      }));
    }
  }, [selectedTermNo, defaultAnswers, answersMap]);

  useEffect(() => {
    setSelectedTermNo(latestSheet?.term_no ?? 0);
  }, [latestSheet?.term_no]);

  return (
    <>
      <ChallengeSheetsModal
        calling={calling}
        isCreateTopicsFailed={isCreateTopicsFailed}
        isOpen={isOpenChallengeSheet}
        createCareerChallengeSheets={createCareerChallengeSheets}
        createCareerChallengeTopics={createCareerChallengeTopics}
        saveCareerChallengeSheets={saveCareerChallengeSheets}
        submitCareerChallengeSheets={submitCareerChallengeSheets}
        onClose={() => setIsOpenChallengeSheet(false)}
      />
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.inner}>
            <p className={styles.title}>キャリア開拓シート</p>
            <div className={styles.selector}>
              <Selector
                latestTermNo={latestSheet?.term_no ?? 0}
                selectedTermNo={selectedTermNo}
                onChange={setSelectedTermNo}
              />
            </div>
          </div>
        </div>
        <div className={styles.container}>
          {selectedSheet?.careergear_sheet_issues?.map(issue => (
            <Question
              key={issue.id}
              sheetIssue={issue}
              isSheetCompleted={isSheetCompleted}
              getAnswerByQuestionId={getAnswerByQuestionId}
              handleChangeAnswers={handleChangeAnswers}
            />
          ))}
        </div>
        {!isSheetCompleted && (
          <div className={styles.footer}>
            <FormActions
              isQuestionsCompletedForTermNo1={isQuestionsCompletedForTermNo1}
              saveCareerSheetAnswers={() =>
                selectedSheet &&
                saveCareerSheetAnswers(selectedSheet.id, currentAnswers)
              }
              applyForCareerMapGeneration={() =>
                selectedSheet && applyForCareerMapGeneration(selectedSheet.id)
              }
              onOpenChallengeSheet={() => setIsOpenChallengeSheet(true)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CareerSheet;
